// localStorage helper functions for authentication state

import type { User, Account, AccountSummary, Permission } from '../types/auth';

// Storage keys
const STORAGE_KEYS = {
  TOKEN: 'aokfin_auth_token',
  REFRESH_TOKEN: 'aokfin_auth_refresh_token',
  REFRESH_EXPIRES_AT: 'aokfin_auth_refresh_expires_at',
  USER: 'aokfin_auth_user',
  ACCOUNT: 'aokfin_auth_account',
  ACCOUNTS: 'aokfin_auth_accounts',
  PERMISSION: 'aokfin_auth_permission',
  EXPIRES_AT: 'aokfin_auth_expires_at',
} as const;

/**
 * Store JWT token
 */
export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
}

/**
 * Get JWT token
 */
export function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

/**
 * Store refresh token
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
}

/**
 * Get refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Store refresh token expiration date
 */
export function setRefreshExpiresAt(expiresAt: Date): void {
  localStorage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, expiresAt.toISOString());
}

/**
 * Get refresh token expiration date
 */
export function getRefreshExpiresAt(): Date | null {
  const expiresAtStr = localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES_AT);
  if (!expiresAtStr) return null;
  try {
    return new Date(expiresAtStr);
  } catch {
    return null;
  }
}

/**
 * Store user information
 */
export function setUser(user: User): void {
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
}

/**
 * Get user information
 */
export function getUser(): User | null {
  const userJson = localStorage.getItem(STORAGE_KEYS.USER);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson) as User;
  } catch {
    return null;
  }
}

/**
 * Store current account
 */
export function setCurrentAccount(account: Account): void {
  localStorage.setItem(STORAGE_KEYS.ACCOUNT, JSON.stringify(account));
}

/**
 * Get current account
 */
export function getCurrentAccount(): Account | null {
  const accountJson = localStorage.getItem(STORAGE_KEYS.ACCOUNT);
  if (!accountJson) return null;
  try {
    return JSON.parse(accountJson) as Account;
  } catch {
    return null;
  }
}

/**
 * Store all accessible accounts
 */
export function setAccounts(accounts: AccountSummary[]): void {
  localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts));
}

/**
 * Get all accessible accounts
 */
export function getAccounts(): AccountSummary[] {
  const accountsJson = localStorage.getItem(STORAGE_KEYS.ACCOUNTS);
  if (!accountsJson) return [];
  try {
    return JSON.parse(accountsJson) as AccountSummary[];
  } catch {
    return [];
  }
}

/**
 * Store permission level
 */
export function setPermission(permission: Permission): void {
  localStorage.setItem(STORAGE_KEYS.PERMISSION, permission);
}

/**
 * Get permission level
 */
export function getPermission(): Permission | null {
  const permission = localStorage.getItem(STORAGE_KEYS.PERMISSION);
  if (!permission) return null;
  return permission as Permission;
}

/**
 * Store token expiration date
 */
export function setExpiresAt(expiresAt: Date): void {
  localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toISOString());
}

/**
 * Get token expiration date
 */
export function getExpiresAt(): Date | null {
  const expiresAtStr = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  if (!expiresAtStr) return null;
  try {
    return new Date(expiresAtStr);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(): boolean {
  const expiresAt = getExpiresAt();
  if (!expiresAt) return true;
  return new Date() >= expiresAt;
}

/**
 * Check if token will expire soon (within 2 minutes)
 * With 15-minute access tokens, we refresh proactively at the 13-minute mark.
 */
export function isTokenExpiringSoon(): boolean {
  const expiresAt = getExpiresAt();
  if (!expiresAt) return true;
  const twoMinutesFromNow = new Date(Date.now() + 2 * 60 * 1000);
  return expiresAt <= twoMinutesFromNow;
}

/**
 * Clear all authentication data from localStorage
 */
export function clearAuth(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = getToken();
  return !!token && !isTokenExpired();
}
